import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faAnglesLeft,
  faAnglesRight,
} from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';

interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

export function Pagination({ page, pageSize, total, onPageChange }: PaginationProps) {
  const totalPages = Math.ceil(total / pageSize);
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  if (total === 0) return null;

  const btnClass = (disabled: boolean) =>
    cn(
      'flex h-8 w-8 items-center justify-center rounded-lg border border-border text-sm transition-colors',
      disabled
        ? 'cursor-not-allowed opacity-40 bg-card text-muted-foreground'
        : 'bg-card text-foreground hover:bg-muted cursor-pointer',
    );

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-muted-foreground">
        Showing{' '}
        <span className="font-medium text-foreground">
          {from}–{to}
        </span>{' '}
        of <span className="font-medium text-foreground">{total}</span> records
      </p>
      <div className="flex items-center gap-1" role="navigation" aria-label="Pagination">
        <button
          className={btnClass(page <= 1)}
          onClick={() => onPageChange(1)}
          disabled={page <= 1}
          aria-label="First page"
        >
          <FontAwesomeIcon icon={faAnglesLeft} className="h-3 w-3" />
        </button>
        <button
          className={btnClass(page <= 1)}
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="h-3 w-3" />
        </button>
        <span className="flex h-8 min-w-[2rem] items-center justify-center rounded-lg bg-primary px-2 text-sm font-medium text-primary-foreground">
          {page}
        </span>
        <span className="px-1 text-sm text-muted-foreground">of {totalPages}</span>
        <button
          className={btnClass(page >= totalPages)}
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          aria-label="Next page"
        >
          <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
        </button>
        <button
          className={btnClass(page >= totalPages)}
          onClick={() => onPageChange(totalPages)}
          disabled={page >= totalPages}
          aria-label="Last page"
        >
          <FontAwesomeIcon icon={faAnglesRight} className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
